import { useState, useEffect } from "react";
import Select from "components/ui/Select";
import Modal from "components/ui/modals";
import { useNavigate } from "react-router-dom";
import { useOrderPrintFormats, useGenerateOrderPrint } from "http-hooks/agorder/useAgorder";
import { useAllSizeGrps } from "http-hooks/sizegrp";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

export default function OrderPrintModal({ isOpen, orderId, onClose }) {
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedSizeGroup, setSelectedSizeGroup] = useState(null);
  const [withRate, setWithRate] = useState(null);
  const [showSizeGroupField, setShowSizeGroupField] = useState(false);
  const [showRateField, setShowRateField] = useState(false);

  const { data: printFormats, isLoading: isLoadingFormats } = useOrderPrintFormats();
  const { data: sizeGroups, isLoading: isLoadingSizeGroups } = useAllSizeGrps({ noPagination: 'true' });
  const { mutateAsync: generatePrint, isPending: isGenerating } = useGenerateOrderPrint();

  const formatOptions = printFormats?.map(format => ({
    value: format.id,
    label: format.name
  })) || [];

  const sizeGroupOptions = sizeGroups?.data?.map(group => ({
    value: group.szgrpid,
    label: group.szgrpname
  })) || [];

  const rateOptions = [
    { value: 0, label: "Without Rate" },
    { value: 1, label: "With Rate" }
  ];

  useEffect(() => {
    if (selectedFormat) {
      const formatId = selectedFormat.value;
      if (formatId === 3) {
        setShowSizeGroupField(true);
        setShowRateField(false);
        setWithRate(null);
      } else if (formatId === 4 || formatId === 5) {
        setShowSizeGroupField(false);
        setShowRateField(true);
        setSelectedSizeGroup(null);
      } else {
        setShowSizeGroupField(false);
        setShowRateField(false);
        setSelectedSizeGroup(null);
        setWithRate(null);
      }
    }
  }, [selectedFormat]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFormat) {
      alert("Please select Print Type");
      return;
    }
    if (selectedFormat.value === 3 && !selectedSizeGroup) {
      alert("Please select Size Group");
      return;
    }
    if ((selectedFormat.value === 4 || selectedFormat.value === 5) && withRate === null) {
      alert("Please select With/Without Rate");
      return;
    }

    try {
      const data = await generatePrint({
        orderId,
        formatId: selectedFormat.value,
        sizeGroupId: selectedSizeGroup?.value || null,
        withRate: withRate?.value !== undefined ? withRate.value : null
      });

      const { htmlUrl, pdfUrl } = getDocumentUrls(data);
      const route = getDocumentViewerRoute(orderId, {
        htmlUrl,
        pdfUrl,
        documentNumber: orderId,
        documentType: 'Order'
      });

      navigate(route);
      onClose();
    } catch (error) {
      console.error("Error generating order print:", error);
    }
  };

  return (
    <Modal
      open={isOpen}
      setOpen={(val) => !val && onClose()}
      header={<h3 className="text-lg font-semibold leading-6 text-gray-900">Print Order</h3>}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isGenerating}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="order-print-form"
            disabled={isGenerating || !selectedFormat}
            className="rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Generating..." : "Generate Print"}
          </button>
        </div>
      }
    >
      <form id="order-print-form" onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="Print Type"
          options={formatOptions}
          value={selectedFormat}
          onChange={setSelectedFormat}
          placeholder="Choose print format"
          isRequired={true}
          isLoading={isLoadingFormats}
        />

        {showSizeGroupField && (
          <Select
            label="Size Group"
            options={sizeGroupOptions}
            value={selectedSizeGroup}
            onChange={setSelectedSizeGroup}
            placeholder="Select a size group"
            isLoading={isLoadingSizeGroups}
            isRequired={true}
          />
        )}

        {showRateField && (
          <Select
            label="With/Without Rate"
            options={rateOptions}
            value={withRate}
            onChange={setWithRate}
            placeholder="Select rate option"
            isRequired={true}
          />
        )}
      </form>
    </Modal>
  );
}
